import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

interface ModelViewerProps {
  fileData?: ArrayBuffer;
  fileName: string;
  issues?: Array<{
    position: [number, number, number];
    type: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
  }>;
  className?: string;
}

export const ModelViewer: React.FC<ModelViewerProps> = ({
  fileData,
  issues = [],
  className = ''
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const modelRef = useRef<THREE.Mesh>();
  const frameRef = useRef<number>();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWireframe, setShowWireframe] = useState(false);

  // Mouse interaction state
  const mouseRef = useRef({
    isDown: false,
    lastX: 0,
    lastY: 0,
    rotationX: 0,
    rotationY: 0,
    zoom: 5
  });

  useEffect(() => {
    if (!mountRef.current) return;

    try {
      // Set up Three.js scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf8f9fa);

      // Set up camera
      const camera = new THREE.PerspectiveCamera(
        75,
        mountRef.current.clientWidth / mountRef.current.clientHeight,
        0.1,
        1000
      );

      // Set up renderer
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      });
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      mountRef.current.appendChild(renderer.domElement);

      // Add lights
      const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 10, 5);
      directionalLight.castShadow = true;
      scene.add(directionalLight);

      // Set up camera position
      camera.position.set(5, 5, 5);
      camera.lookAt(0, 0, 0);

      sceneRef.current = scene;
      rendererRef.current = renderer;
      cameraRef.current = camera;

      // Load model if file data is provided
      if (fileData) {
        loadSTLModel(scene, fileData);
      } else {
        setIsLoading(false);
      }

      // Add mouse controls
      setupMouseControls(renderer.domElement, camera);

      // Start render loop
      const animate = () => {
        frameRef.current = requestAnimationFrame(animate);
        renderer.render(scene, camera);
      };
      animate();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize 3D viewer');
      setIsLoading(false);
    }

    // Cleanup
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      if (mountRef.current && rendererRef.current?.domElement) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
      rendererRef.current?.dispose();
    };
  }, [fileData]);

  // Update wireframe when toggle changes
  useEffect(() => {
    if (modelRef.current && modelRef.current.material) {
      (modelRef.current.material as THREE.MeshStandardMaterial).wireframe = showWireframe;
    }
  }, [showWireframe]);

  const loadSTLModel = async (scene: THREE.Scene, data: ArrayBuffer) => {
    try {
      setIsLoading(true);

      // Parse STL file (simplified ASCII parser)
      const text = new TextDecoder().decode(data);
      const geometry = parseSTL(text);

      if (!geometry) {
        throw new Error('Failed to parse STL file');
      }

      // Create material
      const material = new THREE.MeshStandardMaterial({
        color: 0x4f46e5,
        metalness: 0.1,
        roughness: 0.3,
      });

      // Create mesh
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      // Center the model
      geometry.computeBoundingBox();
      const box = geometry.boundingBox!;
      const center = box.getCenter(new THREE.Vector3());
      geometry.translate(-center.x, -center.y, -center.z);

      // Scale camera distance based on model size
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 2;

      if (cameraRef.current) {
        cameraRef.current.position.set(distance, distance, distance);
        cameraRef.current.lookAt(0, 0, 0);
        mouseRef.current.zoom = distance;
      }

      scene.add(mesh);
      modelRef.current = mesh;

      // Add issue markers
      addIssueMarkers(scene, issues);

      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load model');
      setIsLoading(false);
    }
  };

  const parseSTL = (text: string): THREE.BufferGeometry | null => {
    try {
      const geometry = new THREE.BufferGeometry();
      const vertices: number[] = [];
      const normals: number[] = [];

      // Simple ASCII STL parser
      const lines = text.split('\n');
      let currentNormal: number[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (line.startsWith('facet normal')) {
          const parts = line.split(' ');
          currentNormal = [
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parseFloat(parts[4])
          ];
        } else if (line.startsWith('vertex')) {
          const parts = line.split(' ');
          vertices.push(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3])
          );
          normals.push(...currentNormal);
        }
      }

      if (vertices.length === 0) {
        return null;
      }

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      geometry.computeBoundingBox();

      return geometry;
    } catch (err) {
      console.error('STL parsing error:', err);
      return null;
    }
  };

  const addIssueMarkers = (scene: THREE.Scene, issues: ModelViewerProps['issues']) => {
    issues?.forEach((issue, index) => {
      const color = issue.severity === 'high' ? 0xff4444 :
                   issue.severity === 'medium' ? 0xffaa44 : 0x44aaff;

      const geometry = new THREE.SphereGeometry(0.1, 8, 8);
      const material = new THREE.MeshBasicMaterial({ color });
      const marker = new THREE.Mesh(geometry, material);

      marker.position.set(issue.position[0], issue.position[1], issue.position[2]);
      marker.userData = { issue, index };

      scene.add(marker);
    });
  };

  const setupMouseControls = (canvas: HTMLCanvasElement, camera: THREE.PerspectiveCamera) => {
    const handleMouseDown = (event: MouseEvent) => {
      mouseRef.current.isDown = true;
      mouseRef.current.lastX = event.clientX;
      mouseRef.current.lastY = event.clientY;
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!mouseRef.current.isDown) return;

      const deltaX = event.clientX - mouseRef.current.lastX;
      const deltaY = event.clientY - mouseRef.current.lastY;

      mouseRef.current.rotationY += deltaX * 0.01;
      mouseRef.current.rotationX += deltaY * 0.01;

      // Update camera position
      const distance = mouseRef.current.zoom;
      camera.position.x = distance * Math.sin(mouseRef.current.rotationY) * Math.cos(mouseRef.current.rotationX);
      camera.position.y = distance * Math.sin(mouseRef.current.rotationX);
      camera.position.z = distance * Math.cos(mouseRef.current.rotationY) * Math.cos(mouseRef.current.rotationX);
      camera.lookAt(0, 0, 0);

      mouseRef.current.lastX = event.clientX;
      mouseRef.current.lastY = event.clientY;
    };

    const handleMouseUp = () => {
      mouseRef.current.isDown = false;
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      mouseRef.current.zoom += event.deltaY * 0.01;
      mouseRef.current.zoom = Math.max(1, Math.min(50, mouseRef.current.zoom));

      const distance = mouseRef.current.zoom;
      camera.position.x = distance * Math.sin(mouseRef.current.rotationY) * Math.cos(mouseRef.current.rotationX);
      camera.position.y = distance * Math.sin(mouseRef.current.rotationX);
      camera.position.z = distance * Math.cos(mouseRef.current.rotationY) * Math.cos(mouseRef.current.rotationX);
      camera.lookAt(0, 0, 0);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
    };
  };

  const resetView = () => {
    if (cameraRef.current) {
      mouseRef.current.rotationX = 0;
      mouseRef.current.rotationY = 0;
      mouseRef.current.zoom = 5;

      cameraRef.current.position.set(5, 5, 5);
      cameraRef.current.lookAt(0, 0, 0);
    }
  };

  if (error) {
    return (
      <div className={`model-viewer ${className}`}>
        <div className="w-full h-96 border rounded-lg flex items-center justify-center bg-red-50">
          <div className="text-center">
            <div className="text-red-600 mb-2">⚠️ Error loading 3D model</div>
            <div className="text-sm text-red-500">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`model-viewer ${className}`}>
      <div className="relative">
        <div
          ref={mountRef}
          className="w-full h-96 border rounded-lg bg-gray-50"
          style={{ cursor: 'grab' }}
        />

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <div className="text-sm text-gray-600">Loading 3D model...</div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          🖱️ Click and drag to rotate • Scroll to zoom
        </div>
        <div className="flex space-x-2">
          <button
            onClick={resetView}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Reset View
          </button>
          <button
            onClick={() => setShowWireframe(!showWireframe)}
            className={`px-3 py-1 text-sm border rounded ${
              showWireframe
                ? 'bg-blue-100 border-blue-300 text-blue-700'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            {showWireframe ? 'Solid' : 'Wireframe'}
          </button>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Issue Markers:</h4>
          <div className="flex space-x-4 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
              High severity
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
              Medium severity
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
              Low severity
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
